import type { LensCardData } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';

export type Row = Record<string, unknown>;
export const number = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') {
    return null;
  }
  const n = Number(typeof v === 'object' && 'value' in v ? v.value : v);
  return Number.isFinite(n) ? n : null;
};
export const text = (v: unknown): string =>
  v === null || v === undefined ? 'Unknown' : String(typeof v === 'object' && 'value' in v ? v.value : v);
export const sum = (rows: Array<Row>, key: string): number | null => {
  const values = rows.map((r) => number(r[key])).filter((v): v is number => v !== null);
  return values.length ? values.reduce((a, b) => a + b, 0) : null;
};
export const ratio = (a: number | null, b: number | null): number | null =>
  a === null || b === null || b === 0 ? null : a / b;
export const format = (v: unknown, percent = false) => {
  const n = number(v);
  return n === null
    ? 'Unknown'
    : new Intl.NumberFormat('en-AU', {
        maximumFractionDigits: 2,
        ...(percent ? { style: 'percent' as const } : {}),
      }).format(n);
};
export function joinedCards(cards: ReadonlyArray<LensCardData>, prefix: string, keys: Array<string>) {
  const selected = cards.filter((c) => c.cardId === prefix || c.cardId.startsWith(prefix + '_'));
  const map = new Map<string, Row>();
  selected.forEach((card) =>
    (card.rows ?? []).forEach((r) => {
      const key = JSON.stringify(keys.map((k) => r[k]));
      map.set(key, { ...map.get(key), ...r });
    }),
  );
  return {
    rows: [...map.values()],
    truncated: selected.some((c) => c.truncated),
    problem: selected.find((c) => !['ready', 'empty'].includes(c.status))?.message,
  };
}
export function csv(rows: Array<Row>, columns: Array<string>) {
  const cell = (v: unknown) => csvCell(v);
  return [columns.map(cell).join(','), ...rows.map((r) => columns.map((c) => cell(r[c])).join(','))].join('\r\n');
}
export function download(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function parseSelections(params: URLSearchParams, prefix: string, keys: ReadonlyArray<string>) {
  return Object.fromEntries(keys.map((k) => [k, params.getAll(prefix + k)]));
}
export function updateSearch(existing: string, prefix: string, values: Record<string, string | Array<string>>) {
  const p = new URLSearchParams(existing);
  Object.entries(values).forEach(([k, v]) => {
    p.delete(prefix + k);
    (Array.isArray(v) ? v : [v]).forEach((x) => p.append(prefix + k, x));
  });
  return p.toString();
}
